import React from 'react';
import { User as UserIcon } from 'lucide-react';

interface ProfilePictureProps {
  photoUrl?: string | null;
  displayName: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  className?: string;
  showBorder?: boolean;
}

const sizeClasses: Record<NonNullable<ProfilePictureProps['size']>, string> = {
  small: 'w-8 h-8 text-sm',
  medium: 'w-10 h-10 text-base',
  large: 'w-12 h-12 text-lg',
  xlarge: 'w-16 h-16 text-xl',
};

const iconSizes: Record<NonNullable<ProfilePictureProps['size']>, number> = {
  small: 16,
  medium: 20,
  large: 24,
  xlarge: 32,
};

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  photoUrl,
  displayName,
  size = 'medium',
  className = '',
  showBorder = true,
}) => {
  const getInitials = (name: string): string => {
    if (!name) return 'U';

    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length === 0) return 'U';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const initials = getInitials(displayName);
  const borderClass = showBorder ? 'ring-2 ring-white dark:ring-gray-700' : '';

  if (photoUrl) {
    return (
      <div
        className={`${sizeClasses[size]} ${borderClass} ${className} rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0`}
      >
        <img
          src={photoUrl}
          alt={displayName}
          className="w-full h-full object-cover"
          onError={(event) => {
            (event.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClasses[size]} ${borderClass} ${className} rounded-full overflow-hidden bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0`}
    >
      {initials !== 'U' ? (
        <span className="text-white font-semibold">{initials}</span>
      ) : (
        <UserIcon className="text-white" size={iconSizes[size]} />
      )}
    </div>
  );
};

export default ProfilePicture;


